import React, { useState, useMemo } from "react";
import linksData from "./LinkData.json";
import PageTitle from "../../../components/pagetitle/PageTitle";
import {
  Box,
  Container,
  Typography,
  Tabs,
  Tab,
  Paper,
  Grid,
  TextField,
  InputAdornment,
  List,
  ListItem,
  ListItemIcon,
  ListItemText,
  ListItemButton,
  Chip,
  Divider,
  useTheme,
  useMediaQuery,
} from "@mui/material";
import SearchIcon from "@mui/icons-material/Search";
import LinkIcon from "@mui/icons-material/Link";
import LaunchIcon from "@mui/icons-material/Launch";
import BookmarkIcon from "@mui/icons-material/Bookmark";

const Links = () => {
  const [activeTab, setActiveTab] = useState(0);
  const [searchTerm, setSearchTerm] = useState("");
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down("sm"));

  const handleChange = (event, newValue) => {
    setActiveTab(newValue);
  };

  const filteredData = useMemo(() => {
    if (!searchTerm) return linksData;

    return linksData
      .map((tab) => ({
        ...tab,
        categories: tab.categories
          .map((cat) => ({
            ...cat,
            items: cat.items.filter((item) =>
              item.label.toLowerCase().includes(searchTerm.toLowerCase())
            ),
          }))
          .filter((cat) => cat.items.length > 0),
      }))
      .filter((tab) => tab.categories.length > 0);
  }, [searchTerm]);

  const activeContent = filteredData[activeTab] || filteredData[0];

  return (
    <Box sx={{ bgcolor: "#f8f9fa", minHeight: "100vh", pb: 10 }}>
      <PageTitle
        pageTitle="Resources & Useful Links"
        pagesub="Knowledge Bank"
      />

      <Container
        maxWidth="lg"
        sx={{ mt: { xs: -4, md: -6 }, position: "relative", zIndex: 10 }}
      >
        {/* Search Bar Section */}
        <Paper
          elevation={0}
          sx={{
            p: 2,
            mb: 4,
            borderRadius: "20px",
            boxShadow: "0 10px 30px rgba(0,0,0,0.05)",
            border: "1px solid #eee",
          }}
        >
          <TextField
            fullWidth
            placeholder="Search for links, portals, or departments..."
            variant="outlined"
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
            InputProps={{
              startAdornment: (
                <InputAdornment position="start">
                  <SearchIcon sx={{ color: "var(--primary-color)" }} />
                </InputAdornment>
              ),
              sx: { borderRadius: "15px", bgcolor: "#fcfcfc" },
            }}
          />
        </Paper>

        <Paper
          elevation={0}
          sx={{
            borderRadius: "24px",
            overflow: "hidden",
            boxShadow: "0 10px 40px rgba(0,0,0,0.05)",
            border: "1px solid #eee",
            bgcolor: "#fff",
          }}
        >
          {/* Top Tabs */}
          <Box
            sx={{ borderBottom: 1, borderColor: "divider", bgcolor: "#fff" }}
          >
            <Tabs
              value={activeTab}
              onChange={handleChange}
              variant={isMobile ? "scrollable" : "fullWidth"}
              scrollButtons="auto"
              sx={{
                "& .MuiTabs-indicator": {
                  height: 4,
                  borderRadius: "4px 4px 0 0",
                  backgroundColor: "var(--primary-color)",
                },
                "& .MuiTab-root": {
                  py: 3,
                  fontSize: "1rem",
                  fontWeight: 600,
                  textTransform: "none",
                  color: "#666",
                  "&.Mui-selected": {
                    color: "var(--primary-color)",
                  },
                },
              }}
            >
              {(searchTerm ? filteredData : linksData).map((tab, index) => (
                <Tab key={tab.id} label={tab.title} sx={{ minWidth: 150 }} />
              ))}
            </Tabs>
          </Box>

          {/* Links Content */}
          <Box sx={{ p: { xs: 2, md: 4 } }}>
            {activeContent ? (
              <Grid container spacing={4}>
                {activeContent.categories.map((cat) => (
                  <Grid item xs={12} md={6} key={cat.id}>
                    <Box sx={{ mb: 2 }}>
                      <Box
                        sx={{
                          display: "flex",
                          alignItems: "center",
                          gap: 1,
                          mb: 2,
                        }}
                      >
                        <BookmarkIcon
                          sx={{
                            color: "var(--primary-color)",
                            fontSize: "1.2rem",
                          }}
                        />
                        <Typography
                          variant="h6"
                          sx={{
                            fontWeight: 700,
                            color: "#333",
                            fontFamily: "'Roboto Slab', serif",
                          }}
                        >
                          {cat.title}
                        </Typography>
                        <Chip
                          label={`${cat.items.length} link${
                            cat.items.length !== 1 ? "s" : ""
                          }`}
                          size="small"
                          sx={{
                            ml: "auto",
                            bgcolor: "#f0f4ff",
                            color: "var(--primary-color)",
                            fontWeight: 600,
                          }}
                        />
                      </Box>

                      <Paper
                        variant="outlined"
                        sx={{
                          borderRadius: "16px",
                          overflow: "hidden",
                          borderColor: "#eff1f5",
                        }}
                      >
                        <List disablePadding>
                          {cat.items.map((item, idx) => (
                            <React.Fragment key={idx}>
                              <ListItem disablePadding>
                                <ListItemButton
                                  component="a"
                                  href={item.url}
                                  target="_blank"
                                  rel="noopener noreferrer"
                                  sx={{
                                    py: 2,
                                    "&:hover": {
                                      bgcolor: "#f8faff",
                                      "& .link-icon": {
                                        color: "var(--primary-color)",
                                      },
                                      "& .launch-icon": {
                                        opacity: 1,
                                        transform: "translateX(0)",
                                      },
                                    },
                                  }}
                                >
                                  <ListItemIcon sx={{ minWidth: 40 }}>
                                    <LinkIcon
                                      className="link-icon"
                                      sx={{ color: "#999", transition: "0.2s" }}
                                    />
                                  </ListItemIcon>
                                  <ListItemText
                                    primary={item.label}
                                    primaryTypographyProps={{
                                      fontWeight: 500,
                                      color: "#444",
                                      fontSize: "0.95rem",
                                    }}
                                  />
                                  <LaunchIcon
                                    className="launch-icon"
                                    sx={{
                                      fontSize: "1rem",
                                      color: "var(--primary-color)",
                                      opacity: 0,
                                      transform: "translateX(-10px)",
                                      transition: "all 0.3s ease",
                                    }}
                                  />
                                </ListItemButton>
                              </ListItem>
                              {idx < cat.items.length - 1 && (
                                <Divider
                                  component="li"
                                  sx={{ borderColor: "#f5f5f5" }}
                                />
                              )}
                            </React.Fragment>
                          ))}
                        </List>
                      </Paper>
                    </Box>
                  </Grid>
                ))}
              </Grid>
            ) : (
              <Box sx={{ py: 10, textAlign: "center" }}>
                <Typography variant="h6" sx={{ color: "#999" }}>
                  No results found matching "{searchTerm}"
                </Typography>
              </Box>
            )}
          </Box>
        </Paper>

        <Box sx={{ mt: 6, textAlign: "center" }}>
          <Typography
            variant="body2"
            sx={{
              color: "#777",
              maxWidth: "800px",
              mx: "auto",
              lineHeight: 1.6,
            }}
          >
            Note: These links redirect to external government and professional
            portals. We ensure they are updated, but are not responsible for the
            content or functionality of external sites.
          </Typography>
        </Box>
      </Container>
    </Box>
  );
};

export default Links;
